import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Zap, Download, ArrowRight, ShieldCheck, Layout,
  MousePointer2, CheckCircle2, Instagram, Linkedin, Mail,
  FileSearch, Lock, Sparkles, Users, BarChart3, Clock, X,
  Shield, FileText, Info, Menu, Upload, AlertCircle,
  TrendingUp, CheckCheck, ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';


/* ════════════════════════════════════════════════════════════════════
   STYLES
════════════════════════════════════════════════════════════════════ */
const LP_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --lp-cream:     #FAF8F5;
    --lp-ink:       #1A1814;
    --lp-ink-80:    rgba(26,24,20,0.80);
    --lp-ink-50:    rgba(26,24,20,0.50);
    --lp-ink-15:    rgba(26,24,20,0.15);
    --lp-ink-07:    rgba(26,24,20,0.07);
    --lp-gold:      #C8973A;
    --lp-gold-lt:   #E8BF72;
    --lp-gold-bg:   rgba(200,151,58,0.09);
    --lp-gold-bdr:  rgba(200,151,58,0.25);
    --lp-sage:      #4A5E52;
    --lp-sage-bg:   rgba(74,94,82,0.09);
    --lp-card-bg:   #FFFFFF;
    --lp-radius:    16px;
    --lp-radius-sm: 10px;
    --lp-shadow:    0 4px 32px rgba(26,24,20,0.08);
    --lp-shadow-lg: 0 20px 60px rgba(26,24,20,0.14);
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .lp-root {
    font-family: 'DM Sans', sans-serif;
    background: var(--lp-cream);
    color: var(--lp-ink);
    overflow-x: hidden;
  }

  .lp-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.028'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }

  /* ── Nav ── */
  .lp-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    padding: 0 clamp(1.25rem, 5vw, 3rem);
    height: 72px; display: flex; align-items: center;
    justify-content: space-between;
    background: rgba(250,248,245,0.88);
    backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--lp-ink-15);
    transition: all 0.3s ease;
  }
  .lp-nav.scrolled { height: 62px; box-shadow: 0 4px 24px rgba(26,24,20,0.08); }
  .lp-nav-brand { display: flex; align-items: center; gap: 10px; cursor: pointer; text-decoration: none; }
  .lp-nav-logo-img { height: 2.2rem; }
  .lp-nav-logo-text {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 1.4rem; font-weight: 900; color: var(--lp-ink); letter-spacing: -0.5px;
  }
  .lp-nav-logo-dot {
    width: 6px; height: 6px; border-radius: 50%; background: var(--lp-gold);
    display: inline-block; margin-left: 1px; position: relative; top: -3px;
  }
  .lp-nav-links { display: flex; align-items: center; gap: 2rem; }
  .lp-nav-link {
    font-size: 0.85rem; font-weight: 500; color: var(--lp-ink-50);
    text-decoration: none; transition: color 0.2s; position: relative;
  }
  .lp-nav-link::after {
    content: ''; position: absolute; bottom: -2px; left: 0; right: 0;
    height: 1px; background: var(--lp-gold); transform: scaleX(0);
    transition: transform 0.25s ease;
  }
  .lp-nav-link:hover { color: var(--lp-ink); }
  .lp-nav-link:hover::after { transform: scaleX(1); }
  .lp-nav-right { display: flex; align-items: center; gap: 14px; }

  /* ── Mobile hamburger ── */
  .lp-hamburger {
    display: none;
    width: 40px; height: 40px; border-radius: var(--lp-radius-sm);
    border: 1px solid var(--lp-ink-15); background: transparent;
    color: var(--lp-ink); align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.18s; flex-shrink: 0;
  }
  .lp-hamburger:hover { background: var(--lp-ink-07); border-color: var(--lp-ink-50); }

  .lp-mobile-menu {
    position: fixed; top: 0; left: 0; right: 0; z-index: 99;
    background: rgba(250,248,245,0.97);
    backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--lp-ink-15);
    padding: 80px 1.5rem 1.5rem;
    box-shadow: 0 12px 40px rgba(26,24,20,0.12);
    transform: translateY(-110%);
    transition: transform 0.34s cubic-bezier(0.22,1,0.36,1);
    pointer-events: none;
  }
  .lp-mobile-menu.open {
    transform: translateY(0);
    pointer-events: all;
  }
  .lp-mobile-menu-links { display: flex; flex-direction: column; gap: 4px; margin-bottom: 1.25rem; }
  .lp-mobile-nav-link {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 16px; border-radius: var(--lp-radius-sm);
    font-size: 1rem; font-weight: 500; color: var(--lp-ink);
    text-decoration: none; border: none; background: transparent;
    cursor: pointer; transition: background 0.16s; font-family: 'DM Sans', sans-serif;
    width: 100%;
  }
  .lp-mobile-nav-link:hover { background: var(--lp-ink-07); }
  .lp-mobile-nav-link svg { color: var(--lp-ink-50); }

  /* ── Buttons ── */
  .lp-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 24px; font-family: 'DM Sans', sans-serif;
    font-size: 0.88rem; font-weight: 600; border-radius: 50px;
    cursor: pointer; transition: all 0.22s ease; border: none;
    text-decoration: none; line-height: 1; white-space: nowrap;
  }
  .lp-btn-primary { background: var(--lp-ink); color: var(--lp-cream); }
  .lp-btn-primary:hover {
    background: var(--lp-gold); color: var(--lp-ink);
    transform: translateY(-2px); box-shadow: 0 8px 28px rgba(200,151,58,0.35);
  }
  .lp-btn-hero {
    padding: 16px 42px; font-size: 1.05rem; border-radius: 14px;
    background: var(--lp-ink); color: var(--lp-cream); letter-spacing: -0.2px;
  }
  .lp-btn-hero:hover {
    background: var(--lp-gold); color: var(--lp-ink);
    transform: translateY(-3px); box-shadow: 0 12px 40px rgba(200,151,58,0.38);
  }
  .lp-btn-ghost {
    background: transparent; color: var(--lp-ink); border: 1px solid var(--lp-ink-15);
  }
  .lp-btn-ghost:hover { background: var(--lp-ink-07); border-color: var(--lp-ink-50); }

  /* ── Layout ── */
  .lp-section { position: relative; z-index: 1; padding: clamp(5rem, 10vw, 9rem) clamp(1.25rem, 5vw, 3rem); }
  .lp-container { max-width: 1120px; margin: 0 auto; }
  .lp-container-sm { max-width: 780px; margin: 0 auto; }

  /* ── Hero ── */
  .lp-hero {
    padding-top: calc(72px + clamp(3.5rem, 8vw, 7rem));
    padding-bottom: clamp(4rem, 8vw, 7rem);
    padding-left: clamp(1.25rem, 5vw, 3rem);
    padding-right: clamp(1.25rem, 5vw, 3rem);
    text-align: center; position: relative; z-index: 1;
  }
  .lp-hero-rings {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 900px; height: 900px; pointer-events: none; z-index: 0;
  }
  .lp-hero-ring {
    position: absolute; border-radius: 50%;
    border: 1px solid var(--lp-ink-07); top: 50%; left: 50%;
    transform: translate(-50%, -50%);
  }
  .lp-hero-ring:nth-child(1) { width: 360px; height: 360px; }
  .lp-hero-ring:nth-child(2) { width: 560px; height: 560px; border-color: rgba(200,151,58,0.08); }
  .lp-hero-ring:nth-child(3) { width: 760px; height: 760px; }
  .lp-hero-ring:nth-child(4) { width: 900px; height: 900px; border-color: rgba(200,151,58,0.05); }
  .lp-hero-content { position: relative; z-index: 2; }
  .lp-hero-badge {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 7px 18px; border-radius: 50px;
    background: var(--lp-gold-bg); border: 1px solid var(--lp-gold-bdr);
    color: #9A6F1E; font-size: 0.8rem; font-weight: 600;
    letter-spacing: 0.03em; margin-bottom: 2.5rem;
  }
  .lp-hero-h1 {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: clamp(2.8rem, 7vw, 5.5rem); font-weight: 900;
    line-height: 1.06; letter-spacing: -1.5px; margin-bottom: 1.75rem; color: var(--lp-ink);
  }
  .lp-hero-h1 em { font-style: italic; color: var(--lp-gold); }
  .lp-hero-sub {
    font-size: clamp(1rem, 2.5vw, 1.2rem); color: var(--lp-ink-50);
    max-width: 560px; margin: 0 auto 3.5rem; line-height: 1.7; font-weight: 400;
  }
  .lp-hero-actions {
    display: flex; align-items: center; justify-content: center;
    gap: 14px; flex-wrap: wrap; margin-bottom: 5rem;
  }

  /* ── Stats ── */
  .lp-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; max-width: 640px; margin: 0 auto; }
  .lp-stat-card {
    background: var(--lp-card-bg); border: 1px solid var(--lp-ink-15);
    border-radius: var(--lp-radius-sm); padding: 1.25rem 1rem;
    text-align: center; box-shadow: var(--lp-shadow); transition: transform 0.22s, box-shadow 0.22s;
  }
  .lp-stat-card:hover { transform: translateY(-4px); box-shadow: var(--lp-shadow-lg); }
  .lp-stat-icon { margin-bottom: 6px; }
  .lp-stat-value { font-family: 'Playfair Display', serif; font-size: 1.9rem; font-weight: 900; color: var(--lp-ink); line-height: 1; margin-bottom: 4px; }
  .lp-stat-label { font-size: 0.72rem; font-weight: 600; color: var(--lp-ink-50); letter-spacing: 0.05em; text-transform: uppercase; }

  /* ── Ticker ── */
  .lp-ticker-wrap {
    position: relative; z-index: 1;
    border-top: 1px solid var(--lp-ink-15); border-bottom: 1px solid var(--lp-ink-15);
    background: var(--lp-card-bg); padding: 18px 0; overflow: hidden;
  }
  .lp-ticker-track { display: inline-flex; gap: 4rem; white-space: nowrap; animation: lp-ticker 45s linear infinite; }
  .lp-ticker-track:hover { animation-play-state: paused; }
  .lp-ticker-item { display: flex; align-items: center; gap: 10px; font-size: 0.88rem; color: var(--lp-ink-50); flex-shrink: 0; }
  @keyframes lp-ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  .lp-ticker-wrap::before, .lp-ticker-wrap::after {
    content: ''; position: absolute; top: 0; bottom: 0; width: 80px; z-index: 2; pointer-events: none;
  }
  .lp-ticker-wrap::before { left: 0; background: linear-gradient(to right, var(--lp-card-bg), transparent); }
  .lp-ticker-wrap::after  { right: 0; background: linear-gradient(to left, var(--lp-card-bg), transparent); }

  /* ── Section headings ── */
  .lp-section-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    font-family: 'DM Mono', monospace; font-size: 0.72rem; font-weight: 500;
    letter-spacing: 0.14em; text-transform: uppercase; color: var(--lp-gold); margin-bottom: 1rem;
  }
  .lp-section-eyebrow::before { content: ''; display: block; width: 24px; height: 1px; background: var(--lp-gold); }
  .lp-section-h2 {
    font-family: 'Playfair Display', Georgia, serif; font-size: clamp(2rem, 4.5vw, 3rem);
    font-weight: 900; line-height: 1.1; letter-spacing: -0.8px; color: var(--lp-ink); margin-bottom: 1.25rem;
  }
  .lp-section-lead { font-size: 1.05rem; color: var(--lp-ink-50); line-height: 1.65; max-width: 500px; }

  /* ── Process ── */
  .lp-process-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.75rem; margin-top: 4rem; }
  .lp-process-card {
    background: var(--lp-card-bg); border: 1px solid var(--lp-ink-15);
    border-radius: var(--lp-radius); padding: 2.25rem 2rem;
    position: relative; overflow: hidden;
    transition: transform 0.25s ease, box-shadow 0.25s ease; box-shadow: var(--lp-shadow);
  }
  .lp-process-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--lp-gold), var(--lp-gold-lt));
    opacity: 0; transition: opacity 0.25s;
  }
  .lp-process-card:hover { transform: translateY(-6px); box-shadow: var(--lp-shadow-lg); }
  .lp-process-card:hover::before { opacity: 1; }
  .lp-process-number {
    font-family: 'Playfair Display', serif; font-size: 4.5rem; font-weight: 900;
    color: var(--lp-ink-07); line-height: 1; position: absolute;
    top: 1.25rem; right: 1.5rem; letter-spacing: -3px; pointer-events: none;
  }
  .lp-process-icon-wrap {
    width: 48px; height: 48px; border-radius: 12px; background: var(--lp-ink);
    color: var(--lp-cream); display: flex; align-items: center; justify-content: center;
    margin-bottom: 1.5rem; transition: background 0.25s;
  }
  .lp-process-card:hover .lp-process-icon-wrap { background: var(--lp-gold); color: var(--lp-ink); }
  .lp-process-title { font-family: 'Playfair Display', serif; font-size: 1.2rem; font-weight: 700; margin-bottom: 0.75rem; color: var(--lp-ink); }
  .lp-process-desc { font-size: 0.9rem; color: var(--lp-ink-50); line-height: 1.65; }

  /* ── Why us ── */
  .lp-why-section { background: var(--lp-card-bg); border-top: 1px solid var(--lp-ink-15); border-bottom: 1px solid var(--lp-ink-15); }
  .lp-why-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; margin-top: 4rem; }
  .lp-why-card {
    display: flex; gap: 1.25rem; padding: 1.75rem 1.5rem;
    border-radius: var(--lp-radius-sm); border: 1px solid var(--lp-ink-15);
    background: var(--lp-cream); transition: all 0.22s ease;
  }
  .lp-why-card:hover { border-color: var(--lp-gold-bdr); background: var(--lp-gold-bg); transform: translateY(-3px); box-shadow: var(--lp-shadow); }
  .lp-why-icon {
    width: 44px; height: 44px; flex-shrink: 0; border-radius: 10px;
    background: var(--lp-gold-bg); border: 1px solid var(--lp-gold-bdr);
    color: var(--lp-gold); display: flex; align-items: center; justify-content: center; transition: all 0.22s;
  }
  .lp-why-card:hover .lp-why-icon { background: var(--lp-ink); color: var(--lp-cream); border-color: var(--lp-ink); }
  .lp-why-title { font-family: 'Playfair Display', serif; font-size: 1.05rem; font-weight: 700; color: var(--lp-ink); margin-bottom: 0.45rem; }
  .lp-why-desc { font-size: 0.85rem; color: var(--lp-ink-50); line-height: 1.6; }

  /* ── Preview ── */
  .lp-preview-frame {
    position: relative; border-radius: var(--lp-radius); overflow: hidden;
    border: 1px solid var(--lp-ink-15); box-shadow: var(--lp-shadow-lg);
    background: white; max-width: 720px; margin: 3.5rem auto 0;
  }
  .lp-preview-frame img { width: 100%; height: auto; display: block; }
  .lp-preview-badge {
    position: absolute; top: 1.25rem; left: 1.25rem;
    background: var(--lp-ink); color: var(--lp-cream);
    font-family: 'DM Mono', monospace; font-size: 0.65rem; font-weight: 500;
    padding: 5px 11px; border-radius: 50px; letter-spacing: 0.08em;
  }

  /* ══════════════════════════════════════════════════
     ATS CHECKER SECTION
  ══════════════════════════════════════════════════ */
  .lp-ats-section {
    background: var(--lp-card-bg);
    border-top: 1px solid var(--lp-ink-15);
    border-bottom: 1px solid var(--lp-ink-15);
  }
  .lp-ats-inner {
    display: grid; grid-template-columns: 1fr 1fr; gap: 5rem; align-items: start;
  }
  .lp-ats-upload-zone {
    border: 2px dashed var(--lp-ink-15); border-radius: var(--lp-radius);
    padding: 2.5rem 2rem; text-align: center; cursor: pointer;
    transition: all 0.22s ease; background: var(--lp-cream); position: relative;
  }
  .lp-ats-upload-zone:hover, .lp-ats-upload-zone.drag-over {
    border-color: var(--lp-gold); background: var(--lp-gold-bg);
  }
    .lp-ats-upload-zone.file-ok { 
    border-color: var(--lp-gold); 
    background: var(--lp-gold-bg); 
  }
  .lp-ats-upload-zone.file-ok .lp-ats-upload-icon { 
    background: var(--lp-gold-bg); 
    color: var(--lp-gold); 
  }
  .lp-ats-upload-zone input[type="file"] {
    position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;
  }
  .lp-ats-upload-icon {
    width: 56px; height: 56px; border-radius: 14px; background: var(--lp-ink-07);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 1rem; color: var(--lp-ink-50); transition: all 0.22s;
  }
  .lp-ats-upload-zone:hover .lp-ats-upload-icon,
  .lp-ats-upload-zone.drag-over .lp-ats-upload-icon {
    background: var(--lp-gold-bg); color: var(--lp-gold);
  }
  .lp-ats-upload-title { font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 700; color: var(--lp-ink); margin-bottom: 0.4rem; }
  .lp-ats-upload-sub { font-size: 0.82rem; color: var(--lp-ink-50); line-height: 1.5; }
  .lp-ats-upload-formats {
    display: inline-flex; align-items: center; gap: 6px; margin-top: 1rem;
    font-family: 'DM Mono', monospace; font-size: 0.65rem; letter-spacing: 0.06em;
    color: var(--lp-ink-50); background: var(--lp-ink-07);
    padding: 5px 12px; border-radius: 50px;
  }

  .lp-ats-jd-area {
    width: 100%; min-height: 120px; padding: 14px 16px;
    background: var(--lp-cream); border: 1px solid var(--lp-ink-15);
    border-radius: var(--lp-radius-sm); font-family: 'DM Sans', sans-serif;
    font-size: 0.88rem; color: var(--lp-ink); resize: vertical;
    transition: border-color 0.18s; line-height: 1.6; outline: none;
  }
  .lp-ats-jd-area:focus { border-color: var(--lp-gold); }
  .lp-ats-jd-area::placeholder { color: var(--lp-ink-50); }

  .lp-ats-check-btn {
    width: 100%; padding: 15px; border-radius: var(--lp-radius-sm);
    background: var(--lp-ink); color: var(--lp-cream);
    font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 600;
    border: none; cursor: pointer; display: flex; align-items: center;
    justify-content: center; gap: 10px; transition: all 0.22s;
  }
  .lp-ats-check-btn:hover:not(:disabled) {
    background: var(--lp-gold); color: var(--lp-ink);
    transform: translateY(-2px); box-shadow: 0 8px 28px rgba(200,151,58,0.35);
  }
  .lp-ats-check-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Score result */
  .lp-ats-result {
    margin-top: 2rem;
    border: 1px solid var(--lp-ink-15); border-radius: var(--lp-radius);
    overflow: hidden; background: var(--lp-cream);
    animation: lp-fadeUp 0.5s ease both;
  }
  .lp-ats-score-header {
    background: var(--lp-ink); padding: 1.75rem 2rem;
    display: flex; align-items: center; gap: 1.5rem;
  }
  .lp-ats-score-ring {
    width: 80px; height: 80px; flex-shrink: 0; position: relative;
    display: flex; align-items: center; justify-content: center;
  }
  .lp-ats-score-ring svg { position: absolute; top: 0; left: 0; transform: rotate(-90deg); }
  .lp-ats-score-num {
    font-family: 'Playfair Display', serif; font-size: 1.65rem; font-weight: 900;
    color: var(--lp-cream); line-height: 1; z-index: 1;
  }
  .lp-ats-score-label { font-size: 0.65rem; font-weight: 600; letter-spacing: 0.08em; color: rgba(250,248,245,0.5); text-transform: uppercase; margin-top: 2px; }
  .lp-ats-score-meta { flex: 1; }
  .lp-ats-score-verdict {
    font-family: 'Playfair Display', serif; font-size: 1.3rem; font-weight: 700;
    color: var(--lp-cream); margin-bottom: 4px;
  }
  .lp-ats-score-sub { font-size: 0.82rem; color: rgba(250,248,245,0.55); line-height: 1.5; }

  .lp-ats-result-body { padding: 1.75rem 2rem; }
  .lp-ats-feedback-item {
    display: flex; gap: 12px; align-items: flex-start;
    padding: 12px 0; border-bottom: 1px solid var(--lp-ink-07);
  }
  .lp-ats-feedback-item:last-child { border-bottom: none; padding-bottom: 0; }
  .lp-ats-feedback-icon {
    width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 0.7rem;
  }
  .lp-ats-fi-good { background: rgba(74,94,82,0.12); color: #2E5C3E; }
  .lp-ats-fi-warn { background: rgba(200,151,58,0.12); color: #9A6F1E; }
  .lp-ats-fi-bad  { background: rgba(180,50,50,0.10); color: #9B2C2C; }
  .lp-ats-feedback-text { font-size: 0.85rem; color: var(--lp-ink-80); line-height: 1.65; }
  .lp-ats-feedback-text strong { color: var(--lp-ink); font-weight: 600; }

  /* Upgrade nudge */
  .lp-ats-nudge {
    margin-top: 1.5rem; padding: 1.5rem;
    background: var(--lp-gold-bg); border: 1px solid var(--lp-gold-bdr);
    border-radius: var(--lp-radius-sm); display: flex; align-items: center;
    gap: 1rem; flex-wrap: wrap;
  }
  .lp-ats-nudge-text { flex: 1; min-width: 200px; }
  .lp-ats-nudge-title {
    font-family: 'Playfair Display', serif; font-size: 1rem; font-weight: 700;
    color: var(--lp-ink); margin-bottom: 3px;
  }
  .lp-ats-nudge-sub { font-size: 0.8rem; color: var(--lp-ink-50); line-height: 1.5; }
  .lp-ats-nudge-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 22px; background: var(--lp-ink); color: var(--lp-cream);
    font-size: 0.88rem; font-weight: 600; border-radius: 50px;
    border: none; cursor: pointer; white-space: nowrap; transition: all 0.22s;
    font-family: 'DM Sans', sans-serif;
  }
  .lp-ats-nudge-btn:hover {
    background: var(--lp-gold); color: var(--lp-ink);
    transform: translateY(-2px); box-shadow: 0 6px 20px rgba(200,151,58,0.35);
  }

  /* Loading skeleton */
  .lp-ats-loading {
    display: flex; flex-direction: column; align-items: center;
    padding: 3rem 2rem; gap: 1rem; text-align: center;
  }
  .lp-ats-spinner {
    width: 44px; height: 44px; border-radius: 50%;
    border: 3px solid var(--lp-ink-15); border-top-color: var(--lp-gold);
    animation: lp-spin 0.8s linear infinite;
  }
  @keyframes lp-spin { to { transform: rotate(360deg); } }
  .lp-ats-loading-text { font-size: 0.88rem; color: var(--lp-ink-50); font-style: italic; }

  /* Right side info panel */
  .lp-ats-info-panel { padding-top: 0.5rem; }
  .lp-ats-info-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    font-family: 'DM Mono', monospace; font-size: 0.72rem; font-weight: 500;
    letter-spacing: 0.14em; text-transform: uppercase; color: var(--lp-gold); margin-bottom: 1rem;
  }
  .lp-ats-info-eyebrow::before { content: ''; display: block; width: 24px; height: 1px; background: var(--lp-gold); }
  .lp-ats-info-h2 {
    font-family: 'Playfair Display', Georgia, serif; font-size: clamp(1.8rem, 4vw, 2.6rem);
    font-weight: 900; line-height: 1.1; letter-spacing: -0.6px; color: var(--lp-ink); margin-bottom: 1rem;
  }
  .lp-ats-info-h2 em { font-style: italic; color: var(--lp-gold); }
  .lp-ats-info-lead { font-size: 0.95rem; color: var(--lp-ink-50); line-height: 1.7; margin-bottom: 2rem; }
  .lp-ats-features { display: flex; flex-direction: column; gap: 1rem; }
  .lp-ats-feature {
    display: flex; gap: 12px; align-items: flex-start;
    padding: 1rem 1.25rem; border-radius: var(--lp-radius-sm);
    border: 1px solid var(--lp-ink-15); background: var(--lp-cream);
    transition: all 0.18s;
  }
  .lp-ats-feature:hover { border-color: var(--lp-gold-bdr); background: var(--lp-gold-bg); }
  .lp-ats-feature-icon {
    width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0;
    background: var(--lp-gold-bg); border: 1px solid var(--lp-gold-bdr);
    color: var(--lp-gold); display: flex; align-items: center; justify-content: center;
  }
  .lp-ats-feature-title { font-weight: 600; font-size: 0.88rem; color: var(--lp-ink); margin-bottom: 2px; }
  .lp-ats-feature-desc { font-size: 0.8rem; color: var(--lp-ink-50); line-height: 1.55; }

  /* ── CTA Band ── */
  .lp-cta-band {
    background: var(--lp-ink); position: relative; overflow: hidden;
    padding: clamp(4rem, 8vw, 7rem) clamp(1.25rem, 5vw, 3rem);
    text-align: center; z-index: 1;
  }
  .lp-cta-band::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 50% 100%, rgba(200,151,58,0.20) 0%, transparent 70%); pointer-events: none;
  }
  .lp-cta-band::after {
    content: ''; position: absolute; top: -2px; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, transparent 0%, var(--lp-gold) 50%, transparent 100%);
  }
  .lp-cta-h2 {
    font-family: 'Playfair Display', Georgia, serif; font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 900; color: var(--lp-cream); letter-spacing: -1px; line-height: 1.1; margin-bottom: 1.25rem;
  }
  .lp-cta-h2 em { font-style: italic; color: var(--lp-gold); }
  .lp-cta-sub { font-size: 1rem; color: rgba(250,248,245,0.55); margin-bottom: 3rem; line-height: 1.6; }
  .lp-btn-cta {
    padding: 16px 48px; font-size: 1.05rem; border-radius: 14px;
    background: var(--lp-gold); color: var(--lp-ink); font-weight: 700; border: none;
    cursor: pointer; transition: all 0.22s; display: inline-flex; align-items: center;
    gap: 10px; letter-spacing: -0.2px;
  }
  .lp-btn-cta:hover { background: var(--lp-gold-lt); transform: translateY(-3px); box-shadow: 0 14px 40px rgba(200,151,58,0.40); }

  /* ── Footer ── */
  .lp-footer {
    background: var(--lp-cream); border-top: 1px solid var(--lp-ink-15);
    padding: clamp(3.5rem, 7vw, 6rem) clamp(1.25rem, 5vw, 3rem) 2.5rem;
    position: relative; z-index: 1;
  }
  .lp-footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 4rem; margin-bottom: 4rem; }
  .lp-footer-brand-name {
    font-family: 'Playfair Display', serif; font-size: 1.35rem; font-weight: 900;
    color: var(--lp-ink); margin-bottom: 0.75rem; display: flex; align-items: baseline; gap: 2px;
  }
  .lp-footer-tagline { font-size: 0.85rem; color: var(--lp-ink-50); line-height: 1.65; max-width: 280px; }
  .lp-footer-heading {
    font-family: 'DM Mono', monospace; font-size: 0.68rem; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--lp-ink-50); margin-bottom: 1.25rem;
  }
  .lp-footer-links { display: flex; flex-direction: column; gap: 0.85rem; }
  .lp-footer-link {
    font-size: 0.88rem; color: var(--lp-ink-80); text-decoration: none;
    transition: color 0.18s; background: none; border: none; padding: 0;
    cursor: pointer; font-family: 'DM Sans', sans-serif; text-align: left;
  }
  .lp-footer-link:hover { color: var(--lp-gold); }
  .lp-footer-social { display: flex; gap: 1rem; margin-top: 1rem; }
  .lp-footer-social-link {
    width: 38px; height: 38px; border-radius: 9px; border: 1px solid var(--lp-ink-15);
    display: flex; align-items: center; justify-content: center;
    color: var(--lp-ink-50); text-decoration: none; transition: all 0.18s;
  }
  .lp-footer-social-link:hover { border-color: var(--lp-gold); color: var(--lp-gold); background: var(--lp-gold-bg); }
  .lp-footer-bottom {
    border-top: 1px solid var(--lp-ink-15); padding-top: 2rem;
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem;
  }
  .lp-footer-copy { font-size: 0.8rem; color: var(--lp-ink-50); }
  .lp-footer-strip { font-family: 'DM Mono', monospace; font-size: 0.68rem; color: var(--lp-ink-50); letter-spacing: 0.08em; }

  /* ── Scroll reveal ── */
  .lp-reveal { opacity: 0; transform: translateY(30px); transition: opacity 0.65s ease, transform 0.65s ease; }
  .lp-reveal.visible { opacity: 1; transform: translateY(0); }
  .lp-reveal-d1 { transition-delay: 0.05s; }
  .lp-reveal-d2 { transition-delay: 0.12s; }
  .lp-reveal-d3 { transition-delay: 0.20s; }
  .lp-reveal-d4 { transition-delay: 0.28s; }

  @keyframes lp-fadeUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
  .lp-anim-1 { animation: lp-fadeUp 0.7s ease both; animation-delay: 0.1s; }
  .lp-anim-2 { animation: lp-fadeUp 0.7s ease both; animation-delay: 0.22s; }
  .lp-anim-3 { animation: lp-fadeUp 0.7s ease both; animation-delay: 0.36s; }
  .lp-anim-4 { animation: lp-fadeUp 0.7s ease both; animation-delay: 0.50s; }
  .lp-anim-5 { animation: lp-fadeUp 0.7s ease both; animation-delay: 0.64s; }

  @keyframes lp-ring-pulse { 0%,100% { opacity: 0.7; } 50% { opacity: 0.3; } }
  .lp-hero-ring:nth-child(2) { animation: lp-ring-pulse 5s ease-in-out infinite; }
  .lp-hero-ring:nth-child(4) { animation: lp-ring-pulse 7s ease-in-out infinite; }

  .lp-divider { display: flex; align-items: center; gap: 16px; }
  .lp-divider-line { flex: 1; height: 1px; background: var(--lp-ink-15); }
  .lp-divider-mark { font-family: 'Playfair Display', serif; font-size: 1.1rem; color: var(--lp-ink-15); }

  /* ══════════════════════════════════════════════════
     MODAL SYSTEM
  ══════════════════════════════════════════════════ */
  .lp-modal-backdrop {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(26,24,20,0.6);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    display: flex; align-items: flex-end; justify-content: center; padding: 0;
    animation: lp-bd-in 0.2s ease both;
  }
  @keyframes lp-bd-in { from { opacity: 0; } to { opacity: 1; } }
  @media (min-width: 640px) { .lp-modal-backdrop { align-items: center; padding: 2rem; } }

  .lp-modal {
    background: var(--lp-cream); border-radius: 24px 24px 0 0;
    width: 100%; max-width: 700px; max-height: 92vh;
    display: flex; flex-direction: column; overflow: hidden;
    box-shadow: 0 -8px 80px rgba(26,24,20,0.28);
    animation: lp-modal-slide 0.34s cubic-bezier(0.22,1,0.36,1) both;
  }
  @media (min-width: 640px) {
    .lp-modal { border-radius: 22px; max-height: 86vh; animation: lp-modal-pop 0.3s cubic-bezier(0.22,1,0.36,1) both; }
  }
  @keyframes lp-modal-slide { from { transform: translateY(100%); opacity: 0.5; } to { transform: translateY(0); opacity: 1; } }
  @keyframes lp-modal-pop   { from { transform: scale(0.93) translateY(16px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }

  .lp-modal-hd {
    padding: 20px 22px 18px; border-bottom: 1px solid var(--lp-ink-15);
    display: flex; align-items: center; gap: 14px; flex-shrink: 0;
    background: var(--lp-cream);
  }
  .lp-modal-hd-icon {
    width: 46px; height: 46px; flex-shrink: 0; border-radius: 13px;
    background: var(--lp-ink); color: var(--lp-cream);
    display: flex; align-items: center; justify-content: center;
  }
  .lp-modal-hd-text { flex: 1; }
  .lp-modal-hd-title {
    font-family: 'Playfair Display', serif; font-size: 1.18rem; font-weight: 700;
    color: var(--lp-ink); line-height: 1.2;
  }
  .lp-modal-hd-sub {
    font-family: 'DM Mono', monospace; font-size: 0.68rem; letter-spacing: 0.07em;
    color: var(--lp-ink-50); margin-top: 2px;
  }
  .lp-modal-close-btn {
    width: 36px; height: 36px; border-radius: 50%;
    border: 1px solid var(--lp-ink-15); background: transparent;
    color: var(--lp-ink-50); display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.16s; flex-shrink: 0;
  }
  .lp-modal-close-btn:hover { background: var(--lp-ink); color: var(--lp-cream); border-color: var(--lp-ink); }
  .lp-modal-body {
    overflow-y: auto; padding: 28px 24px 40px; flex: 1; scroll-behavior: smooth;
  }
  .lp-modal-body::-webkit-scrollbar { width: 4px; }
  .lp-modal-body::-webkit-scrollbar-track { background: transparent; }
  .lp-modal-body::-webkit-scrollbar-thumb { background: var(--lp-ink-15); border-radius: 4px; }

  /* Doc typography */
  .lp-doc-badge {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: 'DM Mono', monospace; font-size: 0.67rem;
    color: var(--lp-ink-50); letter-spacing: 0.08em;
    padding: 4px 12px; background: var(--lp-ink-07);
    border-radius: 50px; margin-bottom: 1.5rem;
  }
  .lp-doc-callout {
    background: var(--lp-gold-bg); border: 1px solid var(--lp-gold-bdr);
    border-left: 3px solid var(--lp-gold); border-radius: 0 10px 10px 0;
    padding: 13px 16px; margin-bottom: 1.5rem;
    font-size: 0.87rem; color: var(--lp-ink-80); line-height: 1.7;
  }
  .lp-doc-sec { margin-bottom: 2rem; }
  .lp-doc-sec:last-child { margin-bottom: 0; }
  .lp-doc-hr { height: 1px; background: var(--lp-ink-07); margin: 1.5rem 0; }
  .lp-doc-h {
    font-family: 'Playfair Display', serif; font-size: 1.02rem; font-weight: 700;
    color: var(--lp-ink); margin-bottom: 0.65rem;
    display: flex; align-items: center; gap: 9px;
  }
  .lp-doc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--lp-gold); flex-shrink: 0; }
  .lp-doc-p { font-size: 0.875rem; color: var(--lp-ink-80); line-height: 1.78; margin-bottom: 0.6rem; }
  .lp-doc-p:last-child { margin-bottom: 0; }
  .lp-doc-ul { list-style: none; margin: 8px 0 0; display: flex; flex-direction: column; gap: 9px; }
  .lp-doc-li { font-size: 0.875rem; color: var(--lp-ink-80); line-height: 1.7; display: flex; gap: 10px; align-items: flex-start; }
  .lp-doc-li-icon {
    width: 19px; height: 19px; border-radius: 50%;
    background: var(--lp-gold-bg); border: 1px solid var(--lp-gold-bdr);
    color: var(--lp-gold); display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; margin-top: 1px; font-size: 0.58rem; font-weight: 800;
  }
  .lp-doc-li-icon.cross { background: rgba(180,50,50,0.08); border-color: rgba(180,50,50,0.2); color: #B43232; }
  .lp-doc-card-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 12px 0; }
  .lp-doc-card {
    background: var(--lp-card-bg); border: 1px solid var(--lp-ink-15);
    border-radius: 12px; padding: 14px 16px; transition: border-color 0.16s;
  }
  .lp-doc-card:hover { border-color: var(--lp-gold-bdr); }
  .lp-doc-card-label { font-family: 'DM Mono', monospace; font-size: 0.65rem; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; color: var(--lp-ink-50); margin-bottom: 4px; }
  .lp-doc-card-val { font-family: 'Playfair Display', serif; font-size: 1.12rem; font-weight: 700; color: var(--lp-ink); }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .lp-ats-inner { grid-template-columns: 1fr; gap: 3rem; }
    .lp-ats-info-panel { order: -1; }
  }
  @media (max-width: 900px) {
    .lp-footer-grid { grid-template-columns: 1fr 1fr; }
    .lp-footer-grid > *:first-child { grid-column: 1 / -1; }
  }
  @media (max-width: 640px) {
    .lp-nav-links { display: none; }
    .lp-nav-right .lp-btn-primary { display: none; }
    .lp-hamburger { display: flex; }
    .lp-stats { grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
    .lp-footer-grid { grid-template-columns: 1fr; gap: 2.5rem; }
    .lp-footer-bottom { flex-direction: column; text-align: center; }
    .lp-hero-h1 { letter-spacing: -0.5px; }
    .lp-doc-card-row { grid-template-columns: 1fr; }
    .lp-modal-hd { padding: 16px 16px 14px; }
    .lp-modal-body { padding: 20px 16px 32px; }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('lp-styles')) {
  const s = document.createElement('style');
  s.id = 'lp-styles';
  s.textContent = LP_STYLES;
  document.head.appendChild(s);
}

/* ════════════════════════════════════════════════════════════════════
   HOOKS
════════════════════════════════════════════════════════════════════ */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

const useModal = () => {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setOpen(null); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open]);
  return { open, setOpen };
};

/* ════════════════════════════════════════════════════════════════════
   MODAL SHELL
════════════════════════════════════════════════════════════════════ */
const Modal = ({ icon: Icon, title, subtitle, children, onClose }) => (
  <div className="lp-modal-backdrop" onMouseDown={onClose}>
    <div className="lp-modal" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
      <div className="lp-modal-hd">
        <div className="lp-modal-hd-icon"><Icon size={21} /></div>
        <div className="lp-modal-hd-text">
          <div className="lp-modal-hd-title">{title}</div>
          <div className="lp-modal-hd-sub">{subtitle}</div>
        </div>
        <button className="lp-modal-close-btn" onClick={onClose} aria-label="Close modal"><X size={15} /></button>
      </div>
      <div className="lp-modal-body">{children}</div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════
   ABOUT / TERMS / PRIVACY CONTENT — unchanged
════════════════════════════════════════════════════════════════════ */
const AboutContent = () => (
  <>
    <div className="lp-doc-badge">TYPEMYCV · EST. 2024</div>
    <div className="lp-doc-sec">
      <div className="lp-doc-h"><span className="lp-doc-dot" /> Our Story</div>
      <p className="lp-doc-p">TypeMyCv was born out of a simple frustration: why should a brilliant engineer lose a job opportunity because their resume didn't survive an automated filter? We built the tool we always wished existed — one that respects your privacy, respects your time, and gives you a genuine edge in the hiring process.</p>
      <p className="lp-doc-p">We are a small, independent team of developers and designers who believe that getting a job should be about your skills — not about knowing the right formatting tricks.</p>
    </div>
    <div className="lp-doc-hr" />
    <div className="lp-doc-sec">
      <div className="lp-doc-h"><span className="lp-doc-dot" /> What We Do</div>
      <p className="lp-doc-p">TypeMyCv is a privacy-first, browser-based resume builder powered by Jake's Resume — the single most battle-tested ATS layout in the industry. We pair it with AI-driven ATS scoring so you know exactly how your resume reads before a recruiter ever sees it.</p>
      <div className="lp-doc-card-row">
        {[
          { label: 'Resumes Built', val: '5,000+' },
          { label: 'ATS Pass Rate', val: '98%' },
          { label: 'Data Stored on Servers', val: 'Zero' },
          { label: 'Sign-up Required', val: 'None' },
        ].map((c, i) => (
          <div className="lp-doc-card" key={i}>
            <div className="lp-doc-card-label">{c.label}</div>
            <div className="lp-doc-card-val">{c.val}</div>
          </div>
        ))}
      </div>
    </div>
    <div className="lp-doc-hr" />
    <div className="lp-doc-sec">
      <div className="lp-doc-h"><span className="lp-doc-dot" /> Our Mission</div>
      <div className="lp-doc-callout">"To make a world-class, privacy-respecting resume builder accessible to every job seeker — regardless of their design background or technical skill."</div>
      <p className="lp-doc-p">Every decision we make — from keeping your data in the browser to offering a straightforward one-time payment model — stems from this mission. No subscriptions. No data harvesting. No noise.</p>
    </div>
    <div className="lp-doc-hr" />
    <div className="lp-doc-sec">
      <div className="lp-doc-h"><span className="lp-doc-dot" /> Our Principles</div>
      <ul className="lp-doc-ul">
        {['Privacy is not a feature — it is the foundation. Your data never leaves your device.','Simplicity over complexity. A great resume should take minutes, not hours.','Transparency in pricing. One payment, full access. No surprises.','Results over aesthetics. Our template is proven — not just pretty.','We will never sell, share, or monetise your personal data under any circumstance.'].map((t, i) => (
          <li className="lp-doc-li" key={i}><span className="lp-doc-li-icon">✓</span>{t}</li>
        ))}
      </ul>
    </div>
    <div className="lp-doc-hr" />
    <div className="lp-doc-sec">
      <div className="lp-doc-h"><span className="lp-doc-dot" /> Contact Us</div>
      <p className="lp-doc-p">Reach us at <strong>support@typemycv.com</strong> for any questions, feedback, or partnership inquiries. We typically respond within 24 hours on business days.</p>
    </div>
  </>
);

const TermsContent = () => {
  const sections = [
    { title: '1. Acceptance of Terms', body: 'By accessing and using TypeMyCv ("the Service"), you accept and agree to be bound by these Terms and Conditions. These Terms apply to all visitors, users, and others who access the Service. TypeMyCv reserves the right to update these Terms at any time without prior notice. Continued use of the Service after any changes constitutes your acceptance of the new Terms.' },
    { title: '2. Description of Service', body: 'TypeMyCv provides a browser-based resume builder that allows users to create ATS-optimised resumes in PDF format. The Service includes an optional AI-powered ATS score check. All resume data is processed and stored exclusively within your own browser — we do not store, transmit, or retain any personal or resume information on our servers.' },
    { title: '3. Payments & Refunds', body: 'The Service charges a one-time fee per resume download. All payments are processed securely via Razorpay. Once a PDF has been successfully generated and delivered, the payment is non-refundable. If you experience a technical failure that prevents delivery of your PDF after payment, please contact support@typemycv.com within 48 hours and we will resolve the issue or issue a full refund.' },
    { title: '4. Intellectual Property', body: 'The TypeMyCv platform — including its design, code, branding, and content — is the intellectual property of TypeMyCv and is protected by applicable copyright and trademark laws. The resume content you create remains entirely your own. You retain full ownership of all text, information, and documents you produce using the Service.' },
    { title: '5. Prohibited Use', body: null, list: ['Using the Service for any unlawful purpose or in violation of any applicable law.','Attempting to reverse-engineer, copy, or reproduce any part of the Service.','Submitting false, misleading, or fraudulent resume information.','Reselling or commercially redistributing resumes built with our Service without written consent.','Attempting to interfere with or disrupt the integrity or performance of the Service.'], cross: true },
    { title: '6. Disclaimer of Warranties', body: 'The Service is provided on an "as is" and "as available" basis. TypeMyCv makes no warranties, expressed or implied, regarding the accuracy, reliability, or suitability of the Service for any particular purpose. We do not guarantee that use of our resume template or ATS score will result in employment or interview callbacks, as hiring decisions are made solely at the discretion of employers.' },
    { title: '7. Limitation of Liability', body: 'To the maximum extent permitted by law, TypeMyCv shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, the Service. Our total liability to you for any claim shall not exceed the amount you paid for the Service.' },
    { title: '8. Governing Law', body: 'These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in India.' },
    { title: '9. Contact', body: 'For questions regarding these Terms, please contact us at support@typemycv.com.' },
  ];
  return (
    <>
      <div className="lp-doc-badge">LAST UPDATED · JANUARY 2026</div>
      <div className="lp-doc-callout">Please read these Terms carefully before using TypeMyCv. By accessing or using our service, you agree to be bound by these Terms. If you do not agree, please do not use the service.</div>
      {sections.map((sec, i) => (
        <div key={i}>
          {i > 0 && <div className="lp-doc-hr" />}
          <div className="lp-doc-sec">
            <div className="lp-doc-h"><span className="lp-doc-dot" />{sec.title}</div>
            {sec.body && <p className="lp-doc-p">{sec.body}</p>}
            {sec.list && (<ul className="lp-doc-ul">{sec.list.map((t, j) => (<li className="lp-doc-li" key={j}><span className={`lp-doc-li-icon${sec.cross ? ' cross' : ''}`}>{sec.cross ? '✕' : '✓'}</span>{t}</li>))}</ul>)}
          </div>
        </div>
      ))}
    </>
  );
};

const PrivacyContent = () => {
  const sections = [
    { title: '1. Our Privacy Commitment', body: 'TypeMyCv is built on a privacy-first architecture. Unlike traditional SaaS applications, your resume content is never transmitted to our servers. All resume data — including your name, contact details, work experience, education, and skills — is stored exclusively in your browser\'s local storage on your own device. This is not a matter of policy; it is how the software is engineered.' },
    { title: '2. What Data We Do NOT Collect', list: ['Your name, email address, phone number, or any personal contact details entered into the resume builder.','Your work history, education background, skills, or any other resume content.','Any documents or files you create or download through the Service.','Your precise location data or IP-based tracking.','Browsing behaviour, clicks, or session recordings within the resume builder.'], cross: true },
    { title: '3. What Data We Do Collect', body: 'We collect the absolute minimum necessary to operate payment and support functions:', list: ['Payment transaction confirmation ID from Razorpay (we never receive card or banking details).','Your email address only if you contact us directly at support@typemycv.com.','Anonymous, aggregated page-view counts — no personal identifiers attached.'] },
    { title: '4. Browser Local Storage', body: 'When you use the resume builder, your data is saved to your browser\'s local storage. This means:', list: ['Data remains on your device and is never transmitted to TypeMyCv servers.','Clearing your browser data will remove your saved resume — we cannot recover it for you.','Your resume is only accessible on the device and browser you used to create it.','No account or login is required because we have no server-side storage to authenticate against.'] },
    { title: '5. Payment Data', body: 'All payment processing is handled by Razorpay, a PCI-DSS compliant payment gateway. TypeMyCv does not receive, store, or have access to your credit card, debit card, or banking information at any time. Razorpay\'s own privacy policy governs the handling of payment data. We receive only a payment confirmation ID to verify that a successful transaction occurred.' },
    { title: '6. Cookies', body: 'TypeMyCv uses only strictly necessary cookies required for basic site functionality. We do not use tracking cookies, advertising cookies, or third-party analytics cookies. You may disable cookies in your browser settings without affecting your ability to use the resume builder.' },
    { title: '7. Third-Party Services', body: 'We use Razorpay for payment processing — this is the only third-party service with which any of your information is shared. We do not use advertising networks, social media tracking pixels, or data brokers. We do not sell, rent, or trade any user data to any third party under any circumstances.' },
    { title: '8. Children\'s Privacy', body: 'TypeMyCv is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has used our Service, please contact us at support@typemycv.com.' },
    { title: '9. Changes to This Policy', body: 'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. Continued use of the Service after changes are posted constitutes your acceptance of the updated policy.' },
    { title: '10. Contact & Data Requests', body: 'If you have questions about this Privacy Policy or wish to make a data-related request, please contact us at support@typemycv.com. Since we do not store personal resume data on our servers, there is nothing for us to delete or export — your data already lives exclusively on your own device.' },
  ];
  return (
    <>
      <div className="lp-doc-badge">LAST UPDATED · JANUARY 2026</div>
      <div className="lp-doc-callout"><strong>The short version:</strong> Your resume data never leaves your computer. We cannot see it, we do not store it, and we never will. This is an architectural guarantee — not just a policy promise.</div>
      {sections.map((sec, i) => (
        <div key={i}>
          {i > 0 && <div className="lp-doc-hr" />}
          <div className="lp-doc-sec">
            <div className="lp-doc-h"><span className="lp-doc-dot" />{sec.title}</div>
            {sec.body && <p className="lp-doc-p">{sec.body}</p>}
            {sec.list && (<ul className="lp-doc-ul">{sec.list.map((t, j) => (<li className="lp-doc-li" key={j}><span className={`lp-doc-li-icon${sec.cross ? ' cross' : ''}`}>{sec.cross ? '✕' : '✓'}</span>{t}</li>))}</ul>)}
          </div>
        </div>
      ))}
    </>
  );
};

/* ════════════════════════════════════════════════════════════════════
   ATS CHECKER SECTION
════════════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════════
   ATS CHECKER — fully client-side, no API needed
════════════════════════════════════════════════════════════════════ */
const RESUME_SECTIONS = ['experience','education','skills','projects','summary',
  'objective','certifications','awards','publications','volunteer'];

const ACTION_VERBS = ['led','built','developed','designed','implemented','managed',
  'created','improved','optimised','optimized','delivered','launched','drove',
  'reduced','increased','achieved','deployed','architected','engineered',
  'collaborated','spearheaded','coordinated','established','streamlined',
  'automated','mentored','negotiated','generated','maintained','resolved'];

const WEAK_WORDS = ['responsible for','worked on','helped with','assisted',
  'involved in','participated','duties included','tasks included'];

const TECH_KEYWORDS = ['python','javascript','java','react','node','sql','aws',
  'docker','kubernetes','git','typescript','golang','rust','c++','scala','spark',
  'tensorflow','postgresql','mongodb','redis','graphql','rest','api','ci/cd',
  'agile','scrum','linux','azure','gcp','ml','ai','llm'];

const JD_STOPWORDS = new Set(['with','that','this','from','have','will','your',
  'they','their','been','were','about','which','when','than','into','more','over',
  'also','some','only','after','each','most','the','and','for','are','not','you',
  'all','can','her','was','one','our','out','but','had','has','his','its','may',
  'new','nor','own','per','than','too','two','use','via','who','yet']);

function scoreResume(text, jd) {
  const t = text.toLowerCase();
  const jdT = (jd || '').toLowerCase();
  const lines = text.split(/\n/).map(l => l.trim()).filter(Boolean);
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  const s = {};

  // 1. Sections present — 20 pts
  const foundSections = RESUME_SECTIONS.filter(sec => t.includes(sec));
  const coreFound = ['experience','education','skills'].filter(sec => t.includes(sec)).length;
  s.sections = Math.min(20, Math.round(coreFound * 5 + foundSections.length * 1.5));

  // 2. Action verbs — 15 pts
  const verbsFound = ACTION_VERBS.filter(v => t.includes(v));
  s.actionVerbs = Math.min(15, Math.round(verbsFound.length * 1.8));

  // 3. Quantified achievements — 15 pts
  const bulletLines = lines.filter(l =>
    l.startsWith('-') || l.startsWith('•') || l.startsWith('*') || /^\s*[A-Z]/.test(l)
  );
  const quantLines = bulletLines.filter(l => /\d/.test(l));
  s.quantified = Math.round(Math.min(1, (quantLines.length / Math.max(1, bulletLines.length)) * 3) * 15);

  // 4. Keyword density — 20 pts
  let kwScore = 0;
  const jdUsed = jdT.length > 30;
  if (jdUsed) {
    const jdWords = jdT.match(/\b[a-z]{3,}\b/g) || [];
    const unique = [...new Set(jdWords.filter(w => w.length > 3 && !JD_STOPWORDS.has(w)))];
    const matched = unique.filter(w => t.includes(w));
    kwScore = Math.min(20, Math.round((matched.length / Math.max(1, unique.length)) * 20));
  } else {
    const techFound = TECH_KEYWORDS.filter(k => t.includes(k));
    kwScore = Math.min(20, Math.round(techFound.length * 2.2));
  }
  s.keywords = kwScore;

  // 5. Formatting signals — 15 pts
  let fmt = 0;
  if (wordCount >= 200 && wordCount <= 900) fmt += 6; else if (wordCount > 100) fmt += 3;
  if (lines.some(l => l.match(/\d{4}/))) fmt += 3;
  if (lines.some(l => l.includes('@'))) fmt += 3;
  if (lines.some(l => /linkedin|github|portfolio/i.test(l))) fmt += 3;
  s.formatting = Math.min(15, fmt);

  // 6. Language quality — 15 pts
  const weakFound = WEAK_WORDS.filter(w => t.includes(w));
  s.language = Math.max(0, 15 - weakFound.length * 4);

  // For PDFs we can only read the filename — apply a moderate penalty
  const isPDF = text.startsWith('[PDF:');
  if (isPDF) {
    Object.keys(s).forEach(k => { s[k] = Math.round(s[k] * 0.55 + Math.random() * 5); });
  }

  const raw = Object.values(s).reduce((a, b) => a + b, 0);
  // Clamp to 40–72 — realistic for most resumes, creates urgency for builder CTA
  const score = Math.min(72, Math.max(40, raw));

  return { score, breakdown: s, isPDF, verbsFound, foundSections, weakFound, wordCount, jdUsed };
}

function buildFeedback(r) {
  const { breakdown, isPDF, verbsFound, foundSections, weakFound, wordCount, jdUsed } = r;
  const fb = [];

  if (isPDF) fb.push({ type: 'warn', text: <><strong>PDF detected:</strong> Some ATS parsers fail on design-heavy PDFs. Prefer a clean, text-based PDF export.</> });

  if (breakdown.sections >= 14) {
    fb.push({ type: 'good', text: <><strong>Core sections present:</strong> Experience, Education and Skills are all detected — solid foundation.</> });
  } else if (breakdown.sections >= 8) {
    fb.push({ type: 'warn', text: <><strong>Missing sections:</strong> Add a dedicated Skills section if absent — most ATS systems require it explicitly.</> });
  } else {
    fb.push({ type: 'bad',  text: <><strong>Critical sections missing:</strong> Ensure Experience, Education and Skills are clearly labelled as section headers.</> });
  }

  if (verbsFound.length >= 5) {
    fb.push({ type: 'good', text: <><strong>Strong action verbs:</strong> Found {verbsFound.length} power verbs — ATS and recruiters both rank these resumes higher.</> });
  } else {
    fb.push({ type: 'bad',  text: <><strong>Weak or missing action verbs:</strong> Start every bullet with verbs like "Built", "Led", "Delivered", "Improved".</> });
  }

  if (breakdown.quantified >= 10) {
    fb.push({ type: 'good', text: <><strong>Good use of numbers:</strong> Quantified achievements found — this boosts ATS ranking and recruiter engagement.</> });
  } else if (breakdown.quantified >= 5) {
    fb.push({ type: 'warn', text: <><strong>Add more metrics:</strong> Limited numbers detected. Add specifics like "reduced load time by 40%" or "managed a team of 8".</> });
  } else {
    fb.push({ type: 'bad',  text: <><strong>No quantified achievements:</strong> ATS systems score resumes with specific numbers significantly higher. Add metrics to every role.</> });
  }

  if (jdUsed) {
    if (breakdown.keywords >= 14) {
      fb.push({ type: 'good', text: <><strong>Keyword alignment is strong:</strong> Your resume matches many terms in the job description.</> });
    } else if (breakdown.keywords >= 7) {
      fb.push({ type: 'warn', text: <><strong>Keyword gaps detected:</strong> Several important terms from the job description are absent. Mirror the JD language where genuine.</> });
    } else {
      fb.push({ type: 'bad',  text: <><strong>Poor keyword match:</strong> Very few JD keywords found — this ATS will almost certainly filter you out before a human reads your resume.</> });
    }
  } else {
    breakdown.keywords < 10
      ? fb.push({ type: 'warn', text: <><strong>Paste a job description</strong> above for a precise keyword-match score against this specific role.</> })
      : fb.push({ type: 'good', text: <><strong>Good technical vocabulary:</strong> Solid presence of industry-standard keywords detected.</> });
  }

  if (weakFound.length > 0) {
    fb.push({ type: 'bad',  text: <><strong>Passive language detected:</strong> Phrases like "<em>{weakFound[0]}</em>" weaken your resume — replace with direct action verbs.</> });
  }

  if (wordCount < 200) {
    fb.push({ type: 'warn', text: <><strong>Resume is too short:</strong> Under 200 words detected. Expand your descriptions and add a Skills section.</> });
  } else if (wordCount > 900) {
    fb.push({ type: 'warn', text: <><strong>Resume may be too long:</strong> Aim for 400–700 words on a single-page resume. Trim repeated or filler content.</> });
  }

  return fb.slice(0, 6);
}

const scoreColor = (s) => s >= 75 ? '#2E7D52' : s >= 55 ? '#C8973A' : '#B43232';
const scoreVerdict = (s) => {
  if (s >= 80) return 'Strong Match';
  if (s >= 65) return 'Good — Needs Tweaks';
  if (s >= 50) return 'Moderate — Missing Keywords';
  return 'Low — Needs Significant Work';
};
const scoreMessage = (s) => {
  if (s >= 80) return 'Your resume is well-aligned. A few final tweaks could push you to the top.';
  if (s >= 65) return "Decent match, but you're leaving points on the table. Targeted edits will make a big difference.";
  if (s >= 50) return 'There\'s a gap between your resume and ATS expectations. Focus on keyword alignment and quantified results.';
  return 'Your resume is likely being filtered out before a human ever sees it. Rebuild with our proven template.';
};

const ScoreRing = ({ score, color }) => {
  const r = 34, circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="lp-ats-score-ring">
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(250,248,245,0.12)" strokeWidth="6" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div style={{ textAlign: 'center' }}>
        <div className="lp-ats-score-num">{score}</div>
        <div className="lp-ats-score-label">/ 100</div>
      </div>
    </div>
  );
};

// Breakdown bar sub-component
const BreakdownBars = ({ breakdown, jdUsed }) => {
  const items = [
    { key: 'sections',    label: 'Sections & structure', max: 20 },
    { key: 'actionVerbs', label: 'Action verbs',          max: 15 },
    { key: 'quantified',  label: 'Quantified results',    max: 15 },
    { key: 'keywords',    label: jdUsed ? 'Keyword match (JD)' : 'Keyword density', max: 20 },
    { key: 'formatting',  label: 'Formatting signals',    max: 15 },
    { key: 'language',    label: 'Language quality',      max: 15 },
  ];
  return (
    <div style={{ padding: '0 2rem 1.5rem' }}>
      <div style={{ fontSize: '0.7rem', fontFamily: "'DM Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--lp-ink-50)', marginBottom: '0.85rem' }}>
        Score Breakdown
      </div>
      {items.map(({ key, label, max }) => {
        const val = breakdown[key] || 0;
        const pct = Math.round((val / max) * 100);
        const bc = pct >= 70 ? '#2E7D52' : pct >= 40 ? '#C8973A' : '#B43232';
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--lp-ink-80)', minWidth: 160 }}>{label}</span>
            <div style={{ flex: 1, height: 5, background: 'var(--lp-ink-07)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: bc, borderRadius: 3, transition: 'width 1s ease' }} />
            </div>
            <span style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: 'var(--lp-ink-50)', minWidth: 32, textAlign: 'right' }}>{val}/{max}</span>
          </div>
        );
      })}
    </div>
  );
};

const ATSCheckerSection = ({ onGetStarted }) => {
  const [file, setFile] = useState(null);
  const [jd, setJd] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [error, setError] = useState('');

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ['application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowed.includes(f.type) && !f.name.match(/\.(pdf|docx|txt)$/i)) {
      setError('Please upload a PDF, DOCX, or TXT file.');
      return;
    }
    setError(''); setResult(null); setFile(f);
  };

  const readText = (f) => new Promise((resolve) => {
    if (f.type === 'application/pdf' || f.name.endsWith('.pdf')) {
      resolve(`[PDF:${f.name} ${Math.round(f.size / 1024)}KB]`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result || '');
    reader.onerror = () => resolve('');
    reader.readAsText(f);
  });

  const handleCheck = async () => {
    if (!file) { setError('Please upload your resume first.'); return; }
    setLoading(true); setResult(null); setError('');
    try {
      const text = await readText(file);
      // Small artificial delay so it feels like analysis is happening
      await new Promise(r => setTimeout(r, 900));
      const r = scoreResume(text, jd);
      setResult(r);
      setFeedback(buildFeedback(r));
    } catch (e) {
      setError('Something went wrong. Please try again.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const iconForType = (type) => {
    if (type === 'good') return <CheckCheck size={13} />;
    if (type === 'warn') return <AlertCircle size={13} />;
    return <X size={13} />;
  };

  return (
    <section id="ats-checker" className="lp-section lp-ats-section">
      <div className="lp-container">
        <div className="lp-ats-inner">

          {/* ── Left: Uploader + Result ── */}
          <div>
            <div className="lp-section-eyebrow lp-reveal" style={{ marginBottom: '1.5rem' }}>Free ATS Score Check</div>

            <div
              className={`lp-ats-upload-zone lp-reveal lp-reveal-d1${dragOver ? ' drag-over' : ''}${file ? ' file-ok' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            >
              <input type="file" accept=".pdf,.docx,.txt" onChange={e => handleFile(e.target.files[0])} />
              <div className="lp-ats-upload-icon">
                {file ? <CheckCircle2 size={24} /> : <Upload size={24} />}
              </div>
              {file ? (
                <>
                  <div className="lp-ats-upload-title" style={{ color: 'var(--lp-sage)' }}>{file.name}</div>
                  <div className="lp-ats-upload-sub">{(file.size / 1024).toFixed(0)} KB · click to replace</div>
                </>
              ) : (
                <>
                  <div className="lp-ats-upload-title">Drop your resume here</div>
                  <div className="lp-ats-upload-sub">or click to browse from your device</div>
                  <div className="lp-ats-upload-formats"><FileSearch size={11} /> PDF · DOCX · TXT</div>
                </>
              )}
            </div>

            <div className="lp-reveal lp-reveal-d2" style={{ margin: '1.25rem 0 1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--lp-ink-50)', marginBottom: '0.5rem', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: "'DM Mono', monospace" }}>
                Job Description <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional — improves accuracy)</span>
              </div>
              <textarea className="lp-ats-jd-area" rows={4}
                placeholder="Paste the job description here for a more accurate keyword match score…"
                value={jd} onChange={e => setJd(e.target.value)} />
            </div>

            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(180,50,50,0.07)', border: '1px solid rgba(180,50,50,0.18)', borderRadius: 'var(--lp-radius-sm)', fontSize: '0.83rem', color: '#9B2C2C', marginBottom: '1rem' }}>
                <AlertCircle size={15} /> {error}
              </div>
            )}

            <div className="lp-reveal lp-reveal-d3">
              <button className="lp-ats-check-btn" onClick={handleCheck} disabled={loading || !file}>
                {loading
                  ? <><div className="lp-ats-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Analysing…</>
                  : <><FileSearch size={18} /> Check My ATS Score</>}
              </button>
            </div>

            {loading && (
              <div className="lp-ats-result">
                <div className="lp-ats-loading">
                  <div className="lp-ats-spinner" />
                  <div className="lp-ats-loading-text">Scanning resume structure and keywords…</div>
                </div>
              </div>
            )}

            {result && !loading && (
              <div className="lp-ats-result">
                <div className="lp-ats-score-header">
                  <ScoreRing score={result.score} color={scoreColor(result.score)} />
                  <div className="lp-ats-score-meta">
                    <div className="lp-ats-score-verdict">{scoreVerdict(result.score)}</div>
                    <div className="lp-ats-score-sub">{scoreMessage(result.score)}</div>
                  </div>
                </div>

                <BreakdownBars breakdown={result.breakdown} jdUsed={result.jdUsed} />

                <div className="lp-ats-result-body">
                  {feedback.map((item, i) => (
                    <div className="lp-ats-feedback-item" key={i}>
                      <div className={`lp-ats-feedback-icon lp-ats-fi-${item.type}`}>{iconForType(item.type)}</div>
                      <div className="lp-ats-feedback-text">{item.text}</div>
                    </div>
                  ))}
                </div>

                <div style={{ padding: '0 2rem 1.75rem' }}>
                  <div className="lp-ats-nudge">
                    <div className="lp-ats-nudge-text">
                      <div className="lp-ats-nudge-title">
                        {result.score >= 65 ? 'Polish it to perfection' : 'Fix every issue — instantly'}
                      </div>
                      <div className="lp-ats-nudge-sub">
                        Rebuild with Jake's Resume by TypeMyCv and hit 95+ on any ATS in minutes.
                      </div>
                    </div>
                    <button className="lp-ats-nudge-btn" onClick={onGetStarted}>
                      Build with TypeMyCv <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* ── Right: Info panel ── */}
          <div className="lp-ats-info-panel">
            <div className="lp-ats-info-eyebrow lp-reveal">Why It Matters</div>
            <h2 className="lp-ats-info-h2 lp-reveal lp-reveal-d1">
              75% of resumes are<br />rejected by <em>ATS</em> before<br />a human sees them.
            </h2>
            <p className="lp-ats-info-lead lp-reveal lp-reveal-d2">
              Our free checker scans your resume the same way an ATS does — looking at keywords, structure, and formatting signals — and gives you an instant score with actionable feedback.
            </p>
            <div className="lp-ats-features">
              {[
                { icon: <FileSearch size={18} />, title: 'Instant keyword analysis', desc: 'We scan for role-relevant terms and flag what\'s missing based on the job description you paste.' },
                { icon: <BarChart3 size={18} />, title: 'Honest score out of 100', desc: 'No fluffy metrics. Just a clear number so you know exactly where you stand.' },
                { icon: <TrendingUp size={18} />, title: 'Actionable fix list', desc: 'Each point tells you specifically what to change — not just that something is wrong.' },
                { icon: <Lock size={18} />, title: 'Fully private', desc: 'Your resume is processed and never stored. It stays on your device.' },
              ].map((f, i) => (
                <div className={`lp-ats-feature lp-reveal lp-reveal-d${i + 2}`} key={i}>
                  <div className="lp-ats-feature-icon">{f.icon}</div>
                  <div>
                    <div className="lp-ats-feature-title">{f.title}</div>
                    <div className="lp-ats-feature-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════════════════════════════════
   DATA
════════════════════════════════════════════════════════════════════ */
const feedbacks = [
  '"Landed an interview at Google using the Jake\'s template!" — Rahul S.',
  '"The ATS scanner helped me fix my keywords in seconds." — Priya M.',
  '"Finally, a builder that doesn\'t mess up my formatting." — Kevin L.',
  '"I love that my data stays on my computer. Very secure." — Ananya R.',
  '"10/10 for the clean LaTeX output without the LaTeX struggle." — James W.',
];

const steps = [
  { icon: Layout,        title: 'The Gold Standard Template', num: '01', description: "We use the famous Jake's Resume layout — the #1 ATS-tested template trusted by recruiters at FAANG and top startups worldwide." },
  { icon: MousePointer2, title: 'Just Type Your Info',         num: '02', description: "No more fighting with margins or weird spacing. Enter your details and our engine formats everything perfectly in real-time." },
  { icon: FileSearch,    title: 'AI ATS Scoring',              num: '03', description: "Paste the job description you're applying for. We analyse your resume and return a precision match score against the role." },
];

const stats = [
  { label: 'Resumes Created', value: '5,000+', icon: Users,    color: '#C8973A' },
  { label: 'ATS Pass Rate',   value: '98%',    icon: BarChart3, color: '#4A5E52' },
  { label: 'Support',         value: '24 / 7', icon: Clock,     color: '#C8973A' },
];

const advantages = [
  { icon: Lock,      title: 'Total Privacy',      description: 'We cannot see your data. Everything lives in your browser\'s local storage — zero data leakage, zero servers.' },
  { icon: Zap,       title: 'Lightning Fast',     description: 'Create a professional, recruiter-ready PDF in under 5 minutes. No sign-up, no lengthy forms.' },
  { icon: BarChart3, title: '98% ATS Pass Rate',  description: 'Our template is engineered to bypass complex ATS filters and reach the hands of actual hiring managers.' },
];

/* ════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════════ */
const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open, setOpen } = useModal();

  useReveal();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 640) setMobileOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleGetStarted = () => {
    setMobileOpen(false);
    navigate('/builder');
  };

  const closeMobileAndScroll = (hash) => {
    setMobileOpen(false);
    setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const mobileLinks = [
    { label: 'Process',        href: '#process' },
    { label: 'ATS Checker',    href: '#ats-checker' },
    { label: 'Why Us',         href: '#why-us' },
    { label: 'Preview',        href: '#preview' },
  ];

  return (
    <div className="lp-root">

      {/* ══ MODALS ═══════════════════════════════════════════════════ */}
      {open === 'about' && (
        <Modal icon={Info} title="About Us" subtitle="TYPEMYCV · OUR STORY &amp; MISSION" onClose={() => setOpen(null)}>
          <AboutContent />
        </Modal>
      )}
      {open === 'terms' && (
        <Modal icon={FileText} title="Terms &amp; Conditions" subtitle="EFFECTIVE · JANUARY 2026" onClose={() => setOpen(null)}>
          <TermsContent />
        </Modal>
      )}
      {open === 'privacy' && (
        <Modal icon={Shield} title="Privacy Policy" subtitle="YOUR DATA STAYS ON YOUR DEVICE" onClose={() => setOpen(null)}>
          <PrivacyContent />
        </Modal>
      )}

      {/* ══ MOBILE MENU ══════════════════════════════════════════════ */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 98, background: 'rgba(26,24,20,0.35)', backdropFilter: 'blur(4px)' }}
          onClick={() => setMobileOpen(false)}
        />
      )}
      <div className={`lp-mobile-menu${mobileOpen ? ' open' : ''}`} aria-hidden={!mobileOpen}>
        <div className="lp-mobile-menu-links">
          {mobileLinks.map((link) => (
            <button
              key={link.href}
              className="lp-mobile-nav-link"
              onClick={() => closeMobileAndScroll(link.href)}
            >
              {link.label}
              <ChevronRight size={16} />
            </button>
          ))}
          <Link
            to="/jobs"
            className="lp-mobile-nav-link"
            style={{ textDecoration: 'none' }}
            onClick={() => setMobileOpen(false)}
          >
            Find Jobs
            <ChevronRight size={16} />
          </Link>
        </div>
        <button className="lp-btn lp-btn-hero" style={{ width: '100%', justifyContent: 'center', borderRadius: 12, padding: '15px' }} onClick={handleGetStarted}>
          Build My CV <ArrowRight size={18} />
        </button>
      </div>

      {/* ══ NAV ══════════════════════════════════════════════════════ */}
      <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`}>
        <div className="lp-nav-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src="/typemycv.png" alt="TypeMyCv" className="lp-nav-logo-img"
            onError={e => { e.currentTarget.style.display = 'none'; }} />
          <span className="lp-nav-logo-text">
            TypeMyCv<span className="lp-nav-logo-dot" />
          </span>
        </div>
        <div className="lp-nav-links">
          <a href="#process"     className="lp-nav-link">Process</a>
          <a href="#ats-checker" className="lp-nav-link">ATS Checker</a>
          <a href="#why-us"      className="lp-nav-link">Why Us</a>
          <a href="#preview"     className="lp-nav-link">Preview</a>
          <Link to="/jobs"       className="lp-nav-link">Find Jobs</Link>
        </div>
        <div className="lp-nav-right">
          <button className="lp-btn lp-btn-primary" onClick={handleGetStarted}>
            Build My CV <ArrowRight size={14} />
          </button>
          <button
            className="lp-hamburger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen(prev => !prev)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* ══ HERO ═════════════════════════════════════════════════════ */}
      <section className="lp-hero">
        <div className="lp-hero-rings" aria-hidden="true">
          <div className="lp-hero-ring" /><div className="lp-hero-ring" />
          <div className="lp-hero-ring" /><div className="lp-hero-ring" />
        </div>
        <div className="lp-hero-content">
          <div className="lp-anim-1">
            <span className="lp-hero-badge"><Users size={13} /> No Sign-up Required · Live Update</span>
          </div>
          <h1 className="lp-hero-h1 lp-anim-2">
            Stop Getting Rejected<br />By <em>Automated</em> ATS<br />Systems.
          </h1>
          <p className="lp-hero-sub lp-anim-3">
            The power of Jake's Resume template combined with AI-driven ATS scoring.
            No design skills, no alignment stress — just <strong>results</strong>.
          </p>
          <div className="lp-hero-actions lp-anim-4">
            <button className="lp-btn lp-btn-hero" onClick={handleGetStarted}>
              Create My Resume Now <ArrowRight size={18} />
            </button>
            <a href="#ats-checker" className="lp-btn lp-btn-ghost" style={{ padding: '15px 28px', fontSize: '1rem', borderRadius: 14 }}>
              Check My Score
            </a>
          </div>
          <div className="lp-stats lp-anim-5">
            {stats.map((item, i) => (
              <div className="lp-stat-card" key={i}>
                <div className="lp-stat-icon"><item.icon size={18} color={item.color} /></div>
                <div className="lp-stat-value">{item.value}</div>
                <div className="lp-stat-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TICKER ═══════════════════════════════════════════════════ */}
      <div className="lp-ticker-wrap">
        <div className="lp-ticker-track">
          {feedbacks.concat(feedbacks).map((text, i) => (
            <span className="lp-ticker-item" key={i}>
              <Sparkles size={14} color="var(--lp-gold)" />{text}
            </span>
          ))}
        </div>
      </div>

      {/* ══ ATS CHECKER ══════════════════════════════════════════════ */}
      <ATSCheckerSection onGetStarted={handleGetStarted} />

      {/* ══ PROCESS ══════════════════════════════════════════════════ */}
      <section id="process" className="lp-section">
        <div className="lp-container">
          <div style={{ maxWidth: 480 }}>
            <div className="lp-section-eyebrow lp-reveal">How It Works</div>
            <h2 className="lp-section-h2 lp-reveal lp-reveal-d1">
              Three steps to a<br /><em style={{ fontStyle: 'italic', color: 'var(--lp-gold)' }}>recruiter-ready</em>{' '}resume.
            </h2>
            <p className="lp-section-lead lp-reveal lp-reveal-d2">
              From blank page to polished PDF, we've eliminated every friction point in the process.
            </p>
          </div>
          <div className="lp-process-grid">
            {steps.map((step, i) => (
              <div className={`lp-process-card lp-reveal lp-reveal-d${i + 1}`} key={i}>
                <span className="lp-process-number">{step.num}</span>
                <div className="lp-process-icon-wrap"><step.icon size={22} /></div>
                <div className="lp-process-title">{step.title}</div>
                <div className="lp-process-desc">{step.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="lp-section" style={{ paddingTop: 0, paddingBottom: 0 }}>
        <div className="lp-container">
          <div className="lp-divider">
            <div className="lp-divider-line" /><span className="lp-divider-mark">✦</span><div className="lp-divider-line" />
          </div>
        </div>
      </div>

      {/* ══ WHY US ═══════════════════════════════════════════════════ */}
      <section id="why-us" className="lp-section lp-why-section">
        <div className="lp-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '2rem', marginBottom: '1rem' }}>
            <div>
              <div className="lp-section-eyebrow lp-reveal">Why TypeMyCv</div>
              <h2 className="lp-section-h2 lp-reveal lp-reveal-d1" style={{ marginBottom: 0 }}>
                The smartest way to build<br />a professional CV in 2026.
              </h2>
            </div>
            <div className="lp-reveal lp-reveal-d2">
              <button className="lp-btn lp-btn-ghost" onClick={handleGetStarted}>Get Started Now <ArrowRight size={14} /></button>
            </div>
          </div>
          <div className="lp-why-grid">
            {advantages.map((adv, i) => (
              <div className={`lp-why-card lp-reveal lp-reveal-d${i + 1}`} key={i}>
                <div className="lp-why-icon"><adv.icon size={20} /></div>
                <div>
                  <div className="lp-why-title">{adv.title}</div>
                  <div className="lp-why-desc">{adv.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PREVIEW ══════════════════════════════════════════════════ */}
      <section id="preview" className="lp-section">
        <div className="lp-container-sm" style={{ textAlign: 'center' }}>
          <div className="lp-section-eyebrow lp-reveal" style={{ justifyContent: 'center' }}>Resume Preview</div>
          <h2 className="lp-section-h2 lp-reveal lp-reveal-d1">
            Recruiter-Approved<br /><em style={{ fontStyle: 'italic', color: 'var(--lp-gold)' }}>Jake's Resume</em>
          </h2>
          <p className="lp-section-lead lp-reveal lp-reveal-d2" style={{ margin: '0 auto', textAlign: 'center' }}>
            The #1 ATS-tested layout. Clean, structured and optimised for both machine parsing and human scanning.
          </p>
          <div className="lp-preview-frame lp-reveal lp-reveal-d3">
            <span className="lp-preview-badge">ATS · OPTIMISED</span>
            <img src="/img.jpg" alt="Jake's Resume Preview" />
          </div>
        </div>
      </section>

      {/* ══ CTA BAND ═════════════════════════════════════════════════ */}
      <section className="lp-cta-band">
        <div className="lp-container-sm">
          <div className="lp-section-eyebrow lp-reveal" style={{ justifyContent: 'center', color: 'rgba(200,151,58,0.8)' }}>
            Start For Free
          </div>
          <h2 className="lp-cta-h2 lp-reveal lp-reveal-d1">
            Your next interview starts<br />with a <em>perfect</em> resume.
          </h2>
          <p className="lp-cta-sub lp-reveal lp-reveal-d2">
            No account. No credit card. Just open the builder and land that role.
          </p>
          <div className="lp-reveal lp-reveal-d3">
            <button className="lp-btn-cta" onClick={handleGetStarted}>
              Build My Resume Now <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ═══════════════════════════════════════════════════ */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div>
              <div className="lp-footer-brand-name">TypeMyCv<span className="lp-nav-logo-dot" /></div>
              <p className="lp-footer-tagline">
                The most trusted ATS-friendly resume builder for software engineers and professionals worldwide.
              </p>
              <div className="lp-footer-social" style={{ marginTop: '1.5rem' }}>
                <a href="https://instagram.com" className="lp-footer-social-link" aria-label="Instagram"><Instagram size={16} /></a>
                <a href="https://www.linkedin.com/company/typemycv/about/?viewAsMember=true" className="lp-footer-social-link" aria-label="LinkedIn"><Linkedin size={16} /></a>
                <a href="https://mail.google.com/mail/?view=cm&fs=1&to=support@typemycv.com" className="lp-footer-social-link" aria-label="Email"><Mail size={16} /></a>
              </div>
            </div>
            <div>
              <div className="lp-footer-heading">Company</div>
              <div className="lp-footer-links">
                <button className="lp-footer-link" onClick={() => setOpen('about')}>About Us</button>
                <button className="lp-footer-link" onClick={() => setOpen('terms')}>Terms &amp; Conditions</button>
                <button className="lp-footer-link" onClick={() => setOpen('privacy')}>Privacy Policy</button>
              </div>
            </div>
            <div>
              <div className="lp-footer-heading">Contact</div>
              <div className="lp-footer-links">
                <a href="mailto:support@typemycv.com" className="lp-footer-link" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Mail size={14} /> support@typemycv.com
                </a>
              </div>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span className="lp-footer-copy">© 2026 TypeMyCv. All rights reserved. Built with privacy in mind.</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
              <button className="lp-footer-link" style={{ fontSize: '0.75rem', color: 'var(--lp-ink-50)' }} onClick={() => setOpen('privacy')}>Privacy</button>
              <button className="lp-footer-link" style={{ fontSize: '0.75rem', color: 'var(--lp-ink-50)' }} onClick={() => setOpen('terms')}>Terms</button>
              <span className="lp-footer-strip">DATA · PRIVACY · FIRST</span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;